import React, { useState } from "react";
import { LuAlertCircle as AlertCircle, LuCalendar as Calendar } from "react-icons/lu";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { TextArea } from "src/components/Form/textarea";
import { MultiSelect } from "src/components/Form/multi-select";
import { Controller } from "react-hook-form";
import { FileUploadInput, InputFile } from "src/components/Form/upload-input";
import { useAppDispatch } from "src/store";
import { showToast } from "src/store/toast.store";
import { ScheduledPost } from "src/features/feed/types/scheduled-posts.entity";
import { EditScheduledPostData } from "src/features/feed/types/index.type";
import { editScheduledPostSchema } from "src/features/feed/utils/post.validation";
import { useEditScheduledPostMutation } from "src/features/feed/post.service";
import { languageTuples } from "src/utils/util";

type EditScheduledPostProps = {
  post: ScheduledPost;
};

export const EditScheduledPost = ({ post }: EditScheduledPostProps) => {
  const [editScheduledPost] = useEditScheduledPostMutation();

  const dispatch = useAppDispatch();

  const getInputDate = () => {
    const d = post.scheduledDate.toDate();
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(
      2,
      "0"
    )}T${String(d.getHours()).padStart(2, "0")}:${String(d.getMinutes()).padStart(2, "0")}`;
  };
  const {
    register,
    handleSubmit,
    formState: { errors },
    watch,
    control,
  } = useForm<Partial<EditScheduledPostData>>({
    resolver: zodResolver(editScheduledPostSchema),
    defaultValues: {
      langCodes: post.langCodes ?? [],
      content: post.content,
      scheduledDate: getInputDate(),
    },
  });

  const [mediaFiles, setMediaFiles] = useState<Array<InputFile>>([]);
  const [isLoading, setIsLoading] = useState(false);
  const content = watch("content");

  const updateUploadedFile = (files: InputFile[]) => {
    setMediaFiles((prev) => [...prev, ...files]);
  };

  const onSubmit = async (formData: EditScheduledPostData) => {
    try {
      setIsLoading(true);
      console.log(formData);
      await editScheduledPost({
        id: post.id,
        langCodes: formData.langCodes,
        content: formData.content,
        scheduledDate: formData.scheduledDate ? (new Date(formData.scheduledDate).toUTCString() as any) : null,
        ...(mediaFiles.length > 0 && { mediaFiles }),
      }).unwrap();

      dispatch(showToast({ type: "success", message: "Scheduled Post Updated Successfully" }));
    } catch (error) {
      dispatch(
        showToast({ type: "error", message: (error as Record<string, string>).error ?? "something went wrong" })
      );
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <form className="space-y-6" onSubmit={handleSubmit(onSubmit)}>
      <TextArea previewMode={false} registration={register("content")} value={content} label="content" />

      <Controller
        control={control}
        name="langCodes"
        render={({ field: { onChange, value } }) => (
          <MultiSelect
            options={languageTuples.map(([val, label]) => ({ value: val, label }))}
            label="Languages"
            value={value || []}
            onChange={onChange}
            error={errors.langCodes?.message}
          />
        )}
      />

      {/* Schedule Date Section */}
      <div className="space-y-2">
        <label className="block text-sm font-medium text-gray-700">
          <Calendar size={16} className="inline mr-2" />
          Schedule Date & Time
        </label>
        <input
          type="datetime-local"
          required={false}
          {...register("scheduledDate")}
          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
        />
        {errors.scheduledDate && (
          <div className="flex items-center space-x-2 text-red-600 text-sm">
            <AlertCircle size={16} />
            <span>{errors.scheduledDate.message}</span>
          </div>
        )}
      </div>

      {/* Optional: Media Upload Section */}
      <div className="space-y-4">
        <label className="block text-sm font-semibold text-gray-700">Update Media (Optional)</label>
        <FileUploadInput handleInputFile={updateUploadedFile} reset={false} />

        {errors.mediaFiles && (
          <div className="flex items-center space-x-2 text-red-600 text-sm">
            <AlertCircle size={16} />
            <span>Something went wrong when uploading the post images</span>
          </div>
        )}

        {/* Display existing attachments */}
        {post.attachments.length > 0 && (
          <div className="text-sm text-gray-600">Current attachments: {post.attachments.length} file(s)</div>
        )}
      </div>

      {/* Footer Actions */}
      <div className="text-center space-x-4 pt-4 border-t border-gray-200">
        <button
          type="submit"
          disabled={isLoading}
          className="px-6 py-2 rounded-lg text-white bg-blue-600 hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {isLoading ? "Updating..." : "Update Post"}
        </button>
      </div>
    </form>
  );
};
